if (!window.TMGCartDrawerController) {
  window.TMGCartDrawerController = (() => {
    const headerCartSelector =
      '#cart-icon-bubble, .site-header-cart--button, .tmg-sticky-mobile__cartbtn';
    const cartFormSelector = 'form[action*="/cart/add"]';

    const getCartType = () => window.Shopify?.theme?.settings?.cart_type;
    const getDrawer = () => document.querySelector('cart-drawer');
    const getSectionsToRender = () => [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-live-region-text',
      },
    ];

    const getSectionInnerHTML = (html, selector = '.shopify-section') => {
      if (!html) return null;

      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const sectionElement = parsedDocument.querySelector(selector);
      return sectionElement ? sectionElement.innerHTML : null;
    };

    const closeDrawer = () => {
      const drawer = getDrawer();
      if (!drawer) return;

      drawer.classList.remove('active');
      document.body.classList.remove('overflow-hidden');

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(drawer.activeElement);
      }
    };

    const openDrawer = (triggeredBy) => {
      const drawer = getDrawer();
      if (!drawer) return;

      if (triggeredBy) drawer.activeElement = triggeredBy;

      setTimeout(() => {
        drawer.classList.add('animate', 'active');
      });
      document.body.classList.add('overflow-hidden');
    };

    const updateLegacyCartCount = () => {
      if (!window.routes?.cart_url) return;

      fetch(`${window.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => (response.ok ? response.json() : Promise.reject(response)))
        .then((cart) => {
          if (window.TMGCartCount) {
            window.TMGCartCount.render(cart);
            return;
          }

          document.querySelectorAll('[data-header-cart-count]').forEach((countElement) => {
            countElement.textContent = '';
            countElement.dataset.headerCartCount = cart.item_count > 0 ? String(cart.item_count) : '';
            countElement.classList.toggle('visible', cart.item_count > 0);
          });
        })
        .catch((error) => {
          console.error('Unable to refresh the header cart count', error);
        });
    };

    const renderDrawer = (parsedState) => {
      const drawer = getDrawer();
      if (!drawer || !parsedState?.sections) return;

      getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        const sectionHTML = getSectionInnerHTML(parsedState.sections[section.id], section.selector);

        if (sectionElement && sectionHTML !== null) {
          sectionElement.innerHTML = sectionHTML;
        }
      });

      drawer.classList.remove('is-empty');
      const drawerInner = drawer.querySelector('.drawer__inner');
      if (drawerInner) drawerInner.classList.remove('is-empty');

      updateLegacyCartCount();
      openDrawer();
    };

    const ensureDrawerAPI = () => {
      const drawer = getDrawer();
      if (!drawer) return null;

      if (typeof drawer.getSectionsToRender !== 'function') {
        drawer.getSectionsToRender = getSectionsToRender;
      }
      if (typeof drawer.getSectionInnerHTML !== 'function') {
        drawer.getSectionInnerHTML = getSectionInnerHTML;
      }
      if (typeof drawer.setActiveElement !== 'function') {
        drawer.setActiveElement = (element) => {
          drawer.activeElement = element;
        };
      }
      if (typeof drawer.renderContents !== 'function') {
        drawer.renderContents = renderDrawer;
      }
      if (typeof drawer.open !== 'function') {
        drawer.open = openDrawer;
      }
      if (typeof drawer.close !== 'function') {
        drawer.close = closeDrawer;
      }

      return drawer;
    };

    const showFormError = (form, message) => {
      const errorMessage = form.querySelector('[data-cart-add-error]');
      if (!errorMessage) return;

      errorMessage.textContent = message;
      errorMessage.focus();
    };

    const getInventoryError = (form) => {
      const quantityInput = form.querySelector('[data-inventory-available]');
      if (!quantityInput || Number(quantityInput.dataset.inventoryAvailable) > 0) return null;
      return quantityInput.dataset.inventoryMessage;
    };

    const getAddToCartError = (form, response, data) => {
      const responseMessage = data.description || data.message || 'Add to cart failed';
      const isMaximumQuantityError =
        response.status === 422 && /maximum quantity.*already in your cart/i.test(responseMessage);

      if (!isMaximumQuantityError) return responseMessage;

      return form.querySelector('[data-inventory-message]')?.dataset.inventoryMessage || responseMessage;
    };

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!form?.matches?.(cartFormSelector)) return;

        const inventoryError = getInventoryError(form);
        if (!inventoryError) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        showFormError(form, inventoryError);
      },
      true
    );

    const addFromForm = (form, submitButton) => {
      const drawer = ensureDrawerAPI();
      if (!drawer || form.dataset.tmgCartSubmitting === 'true') return;

      const inventoryError = getInventoryError(form);
      if (inventoryError) {
        showFormError(form, inventoryError);
        return;
      }

      form.dataset.tmgCartSubmitting = 'true';
      const previousError = form.querySelector('[data-cart-add-error]');
      if (previousError) previousError.textContent = '';

      if (submitButton) {
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading', 'processing');
      }

      const formData = new FormData(form);
      const sections = getSectionsToRender().map((section) => section.id);
      formData.set('sections', sections.join(','));
      formData.set('sections_url', window.location.pathname);
      drawer.activeElement = submitButton || document.activeElement;

      const config =
        typeof fetchConfig === 'function'
          ? fetchConfig('javascript')
          : {
              method: 'POST',
              headers: { Accept: 'application/json' },
            };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      fetch(window.routes.cart_add_url, config)
        .then((response) =>
          response.json().then((data) => {
            if (!response.ok || data.status) {
              throw new Error(getAddToCartError(form, response, data));
            }
            return data;
          })
        )
        .then((response) => {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'tmg-cart-drawer',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          }

          renderDrawer(response);
        })
        .catch((error) => {
          showFormError(form, error.message);
        })
        .finally(() => {
          delete form.dataset.tmgCartSubmitting;

          if (submitButton) {
            submitButton.removeAttribute('aria-disabled');
            submitButton.classList.remove('loading', 'processing');
          }
        });
    };

    document.addEventListener(
      'submit',
      (event) => {
        if (getCartType() !== 'drawer') return;

        const form = event.target;
        if (!form?.matches?.(cartFormSelector) || !form.querySelector('[name="id"]')) return;
        if (!ensureDrawerAPI()) return;

        event.preventDefault();
        event.stopPropagation();
        addFromForm(form, event.submitter || form.querySelector('[type="submit"]'));
      },
      true
    );

    document.addEventListener('click', (event) => {
      const closeButton = event.target.closest('#CartDrawer-Overlay, .drawer__close');
      if (closeButton?.closest('cart-drawer')) {
        event.preventDefault();
        closeDrawer();
        return;
      }

      if (getCartType() !== 'drawer') return;

      const cartLink = event.target.closest(headerCartSelector);
      if (!cartLink || !ensureDrawerAPI()) return;

      event.preventDefault();
      openDrawer(cartLink);
    });

    document.addEventListener('keydown', (event) => {
      if (getCartType() !== 'drawer' || event.code.toUpperCase() !== 'SPACE') return;

      const cartLink = event.target.closest(headerCartSelector);
      if (!cartLink || !ensureDrawerAPI()) return;

      event.preventDefault();
      openDrawer(cartLink);
    });

    ensureDrawerAPI();

    return {
      addFromForm,
      close: closeDrawer,
      ensureDrawerAPI,
      open: openDrawer,
      renderContents: renderDrawer,
    };
  })();
}

window.TMGCartDrawerBootstrap?.flush(window.TMGCartDrawerController);

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        
        if (!this.form) {
          return;
        }

        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        // Prioritize cart drawer if cart_type is set to "drawer", otherwise use cart notification
        const cartType = window.Shopify?.theme?.settings?.cart_type || 'notification';
        
        if (cartType === 'page') {
          this.cart = null;
        } else if (cartType === 'drawer') {
          this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        } else {
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        }
        
        this.submitButton = this.querySelector('[type="submit"]');
        
        if (this.submitButton) {
          this.submitButtonText = this.submitButton.querySelector('span');
        }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        
        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        
        if (this.cart) {
          const sections = this.cart.getSectionsToRender().map((section) => section.id);
          formData.append('sections', sections);
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) {
                return;
              }
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }
            this.error = false;
            
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }
            
            const spinner = this.querySelector('.loading__spinner');
            if (spinner) {
              spinner.classList.add('hidden');
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) {
          return;
        }

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        
        if (!this.errorMessageWrapper) {
          return;
        }
        
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        const input = this.form.querySelector('[name=id]');
        return input;
      }
    }
  );
}
